/**
 * Field Assist — routes inside the platform shell.
 *
 * One <Route> per screen, each gated on its permission module.
 */
import { lazy, type ReactElement } from 'react';
import { Navigate, Route } from 'react-router-dom';
import type { Action } from '@rsc/shared';
import { RequireAnyPermission, RequirePermission } from '@/components/shared/RequirePermission';
import { orgRoutes } from './areas/org/routes';
import { catalogRoutes } from './areas/catalog/routes';
import { gtmRoutes } from './areas/gtm/routes';
import { commerceRoutes } from './areas/commerce/routes';
import { expenseRoutes } from './areas/expense/routes';
import { journeyRoutes } from './areas/journey/routes';
import { faoneRoutes } from './areas/faone/routes';
import { analyticsRoutes } from './areas/analytics/routes';
import { adminRoutes } from './areas/admin/routes';

const page = <T,>(loader: () => Promise<T>, name: keyof T & string) =>
  lazy(() => loader().then((m) => ({ default: m[name] as unknown as React.ComponentType })));

const Setup = () => import('./pages/SetupPages');
const Outlets = () => import('./pages/OutletPages');
const Journeys = () => import('./pages/JourneyPages');
const Activity = () => import('./pages/ActivityPages');

const TerritoriesPage = page(Setup, 'TerritoriesPage');
const FieldUsersPage = page(Setup, 'FieldUsersPage');
const DistributorsPage = page(Outlets, 'DistributorsPage');
const DistributorDetailPage = page(() => import('./areas/catalog/DistributorDetailPage'), 'DistributorDetailPage');
const OutletsPage = page(Outlets, 'OutletsPage');
const OutletDetailPage = page(Outlets, 'OutletDetailPage');
const BeatsPage = page(Journeys, 'BeatsPage');
const JourneysPage = page(Journeys, 'JourneysPage');
const JourneyDetailPage = page(Journeys, 'JourneyDetailPage');
const VisitsPage = page(Activity, 'VisitsPage');
const AttendancePage = page(Activity, 'AttendancePage');
const TrackingPage = page(Activity, 'TrackingPage');
const SurveysPage = page(() => import('./pages/SurveysPage'), 'SurveysPage');
const ExpensesPage = page(() => import('./pages/ExpensesPage'), 'ExpensesPage');
const OrdersPage = page(() => import('./pages/OrderPages'), 'OrdersPage');
const OrderDetailPage = page(() => import('./pages/OrderPages'), 'OrderDetailPage');
const RequestsPage = page(() => import('./pages/RequestsPage'), 'RequestsPage');
const MyDayPage = page(() => import('./pages/MyDayPage'), 'MyDayPage');
const StockReplenishmentPage = page(() => import('./pages/StockReplenishment'), 'StockReplenishmentPage');
const PriceSheetsPage = page(() => import('./pages/DistributorPortalAdmin'), 'PriceSheetsPage');
const PortalLoginsPage = page(() => import('./pages/DistributorPortalAdmin'), 'PortalLoginsPage');
const RetailerInsightsPage = page(() => import('./pages/DistributorPortalAdmin'), 'RetailerInsightsPage');
const PriceListsPage = page(() => import('./pages/PriceListsPage'), 'PriceListsPage');
const TeamApprovalsPage = page(() => import('./pages/TeamApprovalsPage'), 'TeamApprovalsPage');
const SchemeProgressPage = page(() => import('./pages/SchemeProgressPage'), 'SchemeProgressPage');
const SchemeEligibilityPage = page(() => import('./pages/SchemeEligibilityPage'), 'SchemeEligibilityPage');

const gate = (module: string, what: string, element: ReactElement, action: Action = 'view') => (
  <RequirePermission module={module} action={action} what={what}>{element}</RequirePermission>
);

export function fieldRoutes(): ReactElement[] {
  return [
    <Route key="f-stock" path="field/stock" element={gate('field.distributor', 'distributor stock', <StockReplenishmentPage />)} />,
    <Route key="f-price-sheets" path="field/price-sheets" element={gate('master.pricelist', 'price sheets', <PriceSheetsPage />)} />,
    <Route key="f-price-sheet" path="field/price-sheets/:id" element={gate('master.pricelist', 'price sheets', <PriceSheetsPage />)} />,
    <Route key="f-approvals" path="field/approvals" element={gate('field.visit', 'team approvals', <TeamApprovalsPage />)} />,
    <Route key="f-late-starts" path="field/late-starts" element={<Navigate to="/field/approvals?tab=late" replace />} />,
    <Route key="f-beat-changes" path="field/beat-changes" element={<Navigate to="/field/approvals?tab=beats" replace />} />,
    <Route key="f-scheme-progress" path="field/schemes/progress" element={gate('erp.scheme', 'scheme progress', <SchemeProgressPage />)} />,
    <Route key="f-scheme-eligible" path="field/schemes/:id/eligibility" element={gate('erp.scheme', 'who has earned a scheme', <SchemeEligibilityPage />)} />,
    <Route key="f-price-lists" path="field/price-lists" element={gate('master.pricelist', 'price lists', <PriceListsPage />)} />,
    <Route key="f-portal-logins" path="field/portal-logins" element={gate('admin.user', 'portal logins', <PortalLoginsPage />)} />,
    <Route key="f-retailer-insights" path="field/retailer-insights" element={gate('field.order', 'retailer insights', <RetailerInsightsPage />)} />,
    <Route key="f-my-day" path="field/my-day" element={gate('field.visit', 'your field day', <MyDayPage />, 'create')} />,
    <Route key="f-territories" path="field/territories" element={gate('field.outlet', 'field territories', <TerritoriesPage />)} />,
    <Route key="f-users" path="field/users" element={<RequireAnyPermission modules={['field.master.user', 'field.journey']} what="field users"><FieldUsersPage /></RequireAnyPermission>} />,
    <Route key="f-distributors" path="field/distributors" element={<RequireAnyPermission modules={['field.distributor', 'field.outlet']} what="distributors"><DistributorsPage /></RequireAnyPermission>} />,
    <Route key="f-distributor" path="field/distributors/:id" element={<RequireAnyPermission modules={['field.distributor', 'field.outlet']} what="distributors"><DistributorDetailPage /></RequireAnyPermission>} />,
    <Route key="f-outlets" path="field/outlets" element={gate('field.outlet', 'outlets', <OutletsPage />)} />,
    <Route key="f-outlet" path="field/outlets/:id" element={gate('field.outlet', 'outlets', <OutletDetailPage />)} />,
    <Route key="f-beats" path="field/beats" element={gate('field.beat', 'beats', <BeatsPage />)} />,
    <Route key="f-journeys" path="field/journeys" element={gate('field.journey', 'journey plans', <JourneysPage />)} />,
    <Route key="f-journey" path="field/journeys/:id" element={gate('field.journey', 'journey plans', <JourneyDetailPage />)} />,
    <Route key="f-visits" path="field/visits" element={gate('field.visit', 'visits', <VisitsPage />)} />,
    <Route key="f-attendance" path="field/attendance" element={gate('field.visit', 'field attendance', <AttendancePage />)} />,
    <Route key="f-tracking" path="field/tracking" element={gate('field.tracking', 'live tracking', <TrackingPage />)} />,
    <Route key="f-surveys" path="field/surveys" element={gate('field.survey', 'surveys', <SurveysPage />)} />,
    <Route key="f-expenses" path="field/expenses" element={gate('field.expense', 'field expenses', <ExpensesPage />)} />,
    <Route key="f-orders" path="field/orders" element={gate('field.order', 'field orders', <OrdersPage />)} />,
    <Route key="f-order" path="field/orders/:id" element={gate('field.order', 'field orders', <OrderDetailPage />)} />,
    <Route key="f-requests" path="field/requests" element={<RequireAnyPermission modules={['field.request', 'field.outlet', 'field.journey']} what="general trade requests"><RequestsPage /></RequireAnyPermission>} />,
    // The classic page's three tabs are the Daily summary, User wise sales and
    // the Employee productivity report, each a screen of its own now.
    <Route key="f-reports" path="field/reports" element={<Navigate to="/field/analytics/reports" replace />} />,
    ...orgRoutes(),
    ...catalogRoutes(),
    ...gtmRoutes(),
    ...commerceRoutes(),
    ...expenseRoutes(),
    ...journeyRoutes(),
    ...faoneRoutes(),
    ...analyticsRoutes(),
    ...adminRoutes(),
  ];
}
